import React, { useCallback, useState } from 'react';
import styles from './FavoriteSentencesView.module.css';
import { PlayIcon, HeartIcon } from '../../components/Icons';
import api from '../../services/api';
import WordModal from '../../components/chapter/WordModal/WordModal';

const formatClipLabel = (seconds) => {
  if (!Number.isFinite(seconds)) {
    return '00:00';
  }
  const safe = Math.max(0, seconds);
  const minutes = Math.floor(safe / 60);
  const secs = Math.floor(safe % 60);
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const FavoriteSentencesView = ({
  groups,
  expandedState,
  onToggleGroup,
  onPlayClip,
  onStopClip,
  playingClip,
  onDeleteSentence,
  onStartSentenceStudy,
  loading,
  error,
  emptyCard,
}) => {
  const [showWordModal, setShowWordModal] = useState(false);
  const [wordModalLookup, setWordModalLookup] = useState(null);
  const [wordModalActiveDict, setWordModalActiveDict] = useState('youdao');
  const [dictLoading, setDictLoading] = useState({ youdao: false, collins: false });
  const [dictResults, setDictResults] = useState({});
  const [favoriteLoadingState, setFavoriteLoadingState] = useState(false);
  const [favoriteSuccess, setFavoriteSuccess] = useState(false);
  const [modalContext, setModalContext] = useState({ bookId: null, chapterId: null, sentenceId: null });

  const closeWordModal = useCallback(() => {
    setShowWordModal(false);
    setWordModalLookup(null);
    setDictResults({});
    setDictLoading({ youdao: false, collins: false });
    setFavoriteLoadingState(false);
    setFavoriteSuccess(false);
    setModalContext({ bookId: null, chapterId: null, sentenceId: null });
  }, []);

  const handleWordClick = useCallback(async (rawWord, context = {}) => {
    const trimmed = typeof rawWord === 'string' ? rawWord.trim() : '';
    if (!trimmed) {
      return;
    }

    setShowWordModal(true);
    setWordModalActiveDict('youdao');
    setDictResults({});
    setWordModalLookup({ word: trimmed, success: true });
    setFavoriteLoadingState(false);
    setFavoriteSuccess(false);
    setModalContext((prev) => ({
      bookId: context.bookId ?? prev.bookId ?? null,
      chapterId: context.chapterId ?? prev.chapterId ?? null,
      sentenceId: context.sentenceId ?? prev.sentenceId ?? null,
    }));
    setDictLoading({ youdao: true, collins: true });

    try {
      const [youdaoResponse, collinsResponse] = await Promise.all([
        api.get(`/dict/youdao/lookup/${encodeURIComponent(trimmed)}`).catch(() => ({ data: { success: false } })),
        api.get(`/dict/collins/lookup/${encodeURIComponent(trimmed)}`).catch(() => ({ data: { success: false } })),
      ]);

      setWordModalLookup({
        word: trimmed,
        success: youdaoResponse.data?.success !== false || collinsResponse.data?.success !== false,
      });
      setDictResults({
        youdao: youdaoResponse.data,
        collins: collinsResponse.data,
      });
    } catch (err) {
      console.error('查询单词失败:', err);
      setWordModalLookup({
        word: trimmed,
        success: false,
      });
      setDictResults({});
    } finally {
      setDictLoading({ youdao: false, collins: false });
    }
  }, []);

  const handleModalFavoriteWord = useCallback(async () => {
    if (favoriteSuccess) {
      return;
    }

    const targetWord = wordModalLookup?.word;
    if (!targetWord) {
      return;
    }
    if (favoriteLoadingState) {
      return;
    }

    const chapterId = modalContext.chapterId ? Number(modalContext.chapterId) : null;
    const sentenceId = modalContext.sentenceId ? Number(modalContext.sentenceId) : null;

    if (!chapterId || !sentenceId) {
      alert('缺少章节或句子信息，无法收藏。');
      return;
    }

    setFavoriteLoadingState(true);
    try {
      const youdaoDefinition = dictResults?.youdao?.data?.definition || null;
      const collinsDefinition = dictResults?.collins?.data?.definition || null;

      const payload = {
        chapter_id: chapterId,
        sentence_id: sentenceId,
        word: targetWord,
        word_jsondata: {
          word: targetWord,
          source_book_id: modalContext.bookId,
          source_chapter_id: modalContext.chapterId,
          source_sentence_id: modalContext.sentenceId,
          youdao_definition: youdaoDefinition,
          collins_definition: collinsDefinition,
        },
      };

      const response = await api.post('/favorite-words/', payload);
      if (response.status === 200 || response.status === 201) {
        setFavoriteSuccess(true);
      }
    } catch (error) {
      console.error('收藏单词失败:', error);
      if (error.response?.status === 401) {
        alert('请先登录后再收藏单词');
      } else if (error.response?.status === 409) {
        setFavoriteSuccess(true);
        alert('该单词已收藏');
      } else {
        alert('收藏失败，请稍后重试');
      }
    } finally {
      setFavoriteLoadingState(false);
    }
  }, [dictResults, favoriteLoadingState, favoriteSuccess, modalContext, wordModalLookup]);

  const renderEnglishSentence = useCallback((text, context) => {
    return text.split(/(\s+)/).map((part, index) => {
      if (/^\s+$/.test(part)) {
        return part;
      }
      const cleaned = part.replace(/[^A-Za-z'-]/g, '');
      if (!cleaned) {
        return part;
      }
      const key = `${context.sentenceId || 'sentence'}-word-${index}-${cleaned}`;
      return (
        <span
          key={key}
          className={styles.wordButton}
          onClick={(event) => {
            event.stopPropagation();
            handleWordClick(cleaned, context);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleWordClick(cleaned, context);
            }
          }}
        >
          {part}
        </span>
      );
    });
  }, [handleWordClick]);

  if (!groups.length) {
    return emptyCard;
  }

  return (
    <>
      {loading && <div className={styles.metadataHint}>章节信息加载中...</div>}
      {error && <div className={styles.metadataError}>{error}</div>}
      <div className={styles.groupedWords}>
        {groups.map((bookGroup) => (
          <div key={bookGroup.key} className={styles.bookGroup}>
            <div className={styles.groupHeader}>
              <button
                type="button"
                className={styles.groupToggle}
                onClick={() => onToggleGroup(bookGroup.key)}
                aria-expanded={!!expandedState[bookGroup.key]}
              >
                <span className={styles.caret} aria-hidden="true">
                  {expandedState[bookGroup.key] ? '▾' : '▸'}
                </span>
                <span className={styles.bookGroupTitle}>{bookGroup.bookTitle}</span>
              </button>
              <span className={styles.groupCount}> {bookGroup.itemCount}个</span>
              <button
                type="button"
                className={styles.studyBtn}
                onClick={() => onStartSentenceStudy?.(bookGroup)}
              >
                去学习
              </button>
            </div>
            {expandedState[bookGroup.key] && (
              <div className={styles.bookItemsList} data-scrollable="true">
                {bookGroup.items.map((sentence) => {
                  const hasEnClip = sentence.en_start_timestamp != null && sentence.en_end_timestamp != null;
                  const hasCnClip = sentence.cn_start_timestamp != null && sentence.cn_end_timestamp != null;
                  const isEnPlaying = playingClip.en === sentence.id;
                  const isCnPlaying = playingClip.cn === sentence.id;

                  return (
                    <div key={sentence.id} className={styles.favoriteItem}>
                
                      
                          <div className={styles.favoriteActions}>
                            {(hasEnClip || hasCnClip) && (
                              <div className={styles.sentenceAudioActions}>
                                {hasEnClip && (
                                  <button
                                    type="button"
                                    className={`${styles.audioButton} ${isEnPlaying ? styles.audioButtonActive : ''}`}
                                    onClick={() => (isEnPlaying ? onStopClip('en') : onPlayClip(sentence, 'en'))}
                                    title={`播放英文音频 ${formatClipLabel(sentence.en_start_timestamp)} - ${formatClipLabel(sentence.en_end_timestamp)}`}
                                  >
                                    <PlayIcon size={16} />
                                    <span className={styles.audioButtonLabel}>EN</span>
                                  </button>
                                )}
                                {hasCnClip && (
                                  <button
                                    type="button"
                                    className={`${styles.audioButton} ${isCnPlaying ? styles.audioButtonActive : ''}`}
                                    onClick={() => (isCnPlaying ? onStopClip('cn') : onPlayClip(sentence, 'cn'))}
                                    title={`播放中文音频 ${formatClipLabel(sentence.cn_start_timestamp)} - ${formatClipLabel(sentence.cn_end_timestamp)}`}
                                  >
                                    <PlayIcon size={16} />
                                    <span className={styles.audioButtonLabel}>CN</span>
                                  </button>
                                )}
                              </div>
                            )}
                            <button
                              type="button"
                              className={styles.deleteBtn}
                              onClick={() => onDeleteSentence(sentence.id)}
                              title="取消收藏"
                              aria-label="取消收藏"
                            >
                              <HeartIcon size={20} filled />
                            </button>
                          </div>
                    
                      <div className={styles.sentenceContent}>
                        {sentence.content_zh && (
                          <div className={styles.sentenceZh}>
                            <p className={styles.contentText}>{sentence.content_zh}</p>
                          </div>
                        )}
                        {sentence.content_en && (
                          <div className={styles.sentenceEn}>
                            <p className={styles.contentText}>
                              {renderEnglishSentence(sentence.content_en, {
                                bookId: bookGroup.bookId,
                                chapterId: sentence.chapter_id ?? null,
                                sentenceId: sentence.sentence_id ?? sentence.id ?? null,
                              })}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
      <WordModal
        isOpen={showWordModal}
        wordLookup={wordModalLookup}
        dictResults={dictResults}
        dictLoading={dictLoading}
        favoriteLoading={favoriteLoadingState}
        isFavorited={favoriteSuccess}
        onPlayPronunciation={() => {}}
        onFavoriteWord={handleModalFavoriteWord}
        onDictTabClick={(dict) => setWordModalActiveDict(dict)}
        onClose={closeWordModal}
        onWordLookup={handleWordClick}
        activeDict={wordModalActiveDict}
      />
    </>
  );
};

export default FavoriteSentencesView;
